import Alpine from 'alpinejs';

export const InfiniteScroll = {
  mounted() {
    this.el.addEventListener("scroll", () => {
      if (this.el.scrollTop + this.el.clientHeight >= this.el.scrollHeight - 100) {
        if (this.el.getAttribute("phx-target")) {
          this.pushEventTo(this.el, "load_more", {});
        } else {
          this.pushEvent("load_more", {});
        }
      }
    });
  }
};

export const BackupMealPickerHook = {
  mounted() {
    this._onSelect = (event) => {
      const payload = event.detail || {};
      if (!payload.meal_id) return;
      this.pushEventTo(`#${this.el.id}`, 'select_backup_meal', payload);
    };

    this.el.addEventListener('backup-meal-selected', this._onSelect);
  },

  destroyed() {
    if (this._onSelect) {
      this.el.removeEventListener('backup-meal-selected', this._onSelect);
    }
  }
};

export const ModalManager = {
  mounted() {},
  updated() {}
};

export const LiveSearch = {
  mounted() {},
  updated() {}
};

export const Twemoji = {
  mounted() {
    this.parse();
  },

  updated() {
    this.parse();
  },

  parse() {
    const emojiValue = this.el.dataset.emoji;
    if (!emojiValue) return;

    const codepoint = Array.from(emojiValue)
      .map(char => char.codePointAt(0).toString(16))
      .join('-');

    const img = document.createElement('img');
    img.src = `/images/twemoji/${codepoint}.svg`;
    img.className = 'twemoji';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.draggable = false;
    img.onerror = () => {
      this.el.textContent = emojiValue;
    };

    this.el.innerHTML = '';
    this.el.appendChild(img);
  }
};

export const FlashDismiss = {
  mounted() {
    const duration = parseInt(this.el.dataset.duration || '3000', 10);
    setTimeout(() => {
      this.el.style.animation = 'fadeOut 0.3s ease-out forwards';
      setTimeout(() => this.el.remove(), 300);
    }, duration);
  }
};

export const PreserveCheckboxes = {
  mounted() {
    this.storeCheckboxState();

    const form = this.el.closest('form');
    if (form) {
      this._onSubmit = () => {
        this.storeCheckboxState();
      };
      form.addEventListener('submit', this._onSubmit);
    }
  },

  updated() {
    this.restoreCheckboxState();
  },

  destroyed() {
    if (this._onSubmit) {
      const form = this.el.closest('form');
      if (form) {
        form.removeEventListener('submit', this._onSubmit);
      }
    }
  },

  storeCheckboxState() {
    const checkboxes = this.el.querySelectorAll('input[type="checkbox"]');
    this.checkboxState = {};
    checkboxes.forEach((checkbox) => {
      this.checkboxState[checkbox.value] = checkbox.checked;
    });
  },

  restoreCheckboxState() {
    if (!this.checkboxState) return;
    const checkboxes = this.el.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      if (Object.prototype.hasOwnProperty.call(this.checkboxState, checkbox.value)) {
        checkbox.checked = this.checkboxState[checkbox.value];
      }
    });
  }
};

export const IndeterminateCheckbox = {
  mounted() {
    this.updateIndeterminateState();
  },

  updated() {
    this.updateIndeterminateState();
  },

  updateIndeterminateState() {
    const indeterminate = this.el.dataset.indeterminate === "true";
    this.el.indeterminate = indeterminate;
  }
};

export const AutoDismissFlash = {
  mounted() {
    const timeout = parseInt(this.el?.dataset?.timeout || '5000', 10);
    const key = this.el?.dataset?.kind || 'info';
    this._timer = setTimeout(() => {
      try {
        this.pushEvent('lv:clear-flash', { key });
      } catch (_) { }

      try {
        this.el.style.transition = 'opacity 200ms ease, transform 200ms ease';
        this.el.style.opacity = '0';
        this.el.style.transform = 'translateY(-6px)';
        setTimeout(() => {
          this.el && this.el.remove && this.el.remove();
        }, 220);
      } catch (_) { }
    }, timeout);
  },

  destroyed() {
    try {
      clearTimeout(this._timer);
    } catch (_) { }
  }
};

export const Clipboard = {
  mounted() {
    this.handleEvent('copy_to_clipboard', ({ text, notify }) => {
      const value =
        text && text.startsWith('/')
          ? `${window.location.protocol}//${window.location.host}${text}`
          : (text || '');

      const doCopy = async () => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
      };

      doCopy()
        .then(() => {
          if (notify) {
            try {
              this.pushEvent('copied_to_clipboard', {});
            } catch (_) { }
          }
        })
        .catch(() => {});
    });
  }
};

export const DepartmentTree = {
  mounted() {
    try {
      this._key = this.el?.dataset?.key || 'dept_tree_state';
      const raw = localStorage.getItem(this._key);
      let expanded = [];
      try {
        expanded = JSON.parse(raw) || [];
      } catch (_) {
        expanded = [];
      }

      try {
        this.pushEvent('restore_tree_state', { expanded });
      } catch (_) { }

      this.handleEvent('persist_tree_state', ({ expanded: ex }) => {
        try {
          localStorage.setItem(this._key, JSON.stringify(ex || []));
        } catch (_) { }
      });
    } catch (e) {
      console.warn('DepartmentTree hook init failed:', e);
    }
  }
};

export const PrintPreview = {
  mounted() {
    this.handleEvent("open_print_preview", ({ html, window_name }) => {
      try {
        window.__mealflowPrintWindows = window.__mealflowPrintWindows || {};
        let popup =
          (window_name && window.__mealflowPrintWindows[window_name]) || null;

        if (!popup || popup.closed) {
          popup = window.open('', window_name || '_blank', 'width=1000,height=700,scrollbars=yes');
          if (window_name) {
            window.__mealflowPrintWindows[window_name] = popup;
          }
        }

        if (!popup) {
          alert('Por favor, permita pop-ups para imprimir o documento.');
          return;
        }

        popup.document.open('text/html', 'replace');
        popup.document.write(html || '<html><body>Sem dados</body></html>');
        popup.document.close();
        popup.focus();
      } catch (error) {
        console.error('Print preview failed', error);
      }
    });

    this.handleEvent("download_export", ({ filename, content, mime }) => {
      try {
        if (!content) {
          console.warn('download_export event without content');
          return;
        }

        const binary = atob(content);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
          bytes[i] = binary.charCodeAt(i);
        }

        const blob = new Blob([bytes.buffer], { type: mime || 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename || 'ementa';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) {
        console.error('Download export failed', error);
        alert('Não foi possível descarregar o ficheiro. Consulte o suporte.');
      }
    });
  }
};

export const PrintPreviewTrigger = {
  mounted() {
    this.handleClick = (event) => {
      event.preventDefault();
      const winName = `mf_print_${Date.now()}`;
      const printWindow = window.open('', winName, 'width=1000,height=700,scrollbars=yes');
      if (!printWindow) {
        alert('Por favor, permita pop-ups para imprimir o documento.');
        return;
      }

      window.__mealflowPrintWindows = window.__mealflowPrintWindows || {};
      window.__mealflowPrintWindows[winName] = printWindow;

      const payload = { window_name: winName };
      const targetSelector = this.el.getAttribute("data-target");

      if (targetSelector && targetSelector.trim().length > 0) {
        this.pushEventTo(targetSelector, "show_print_preview", payload);
      } else {
        this.pushEvent("show_print_preview", payload);
      }
    };

    this.el.addEventListener('click', this.handleClick);
  },

  destroyed() {
    if (this.handleClick) {
      this.el.removeEventListener('click', this.handleClick);
    }
  }
};

export const MealSelector = {
  mounted() {
    const focusInput = () => {
      try {
        const el = document.getElementById('meal-search-input');
        if (el) el.focus();
      } catch (_) { }
    };

    this._onKey = (e) => {
      if (e.key === 'Escape') {
        try {
          this.pushEvent('close_meal_selector', {});
        } catch (_) { }
      }
    };

    focusInput();
    setTimeout(focusInput, 50);
    window.addEventListener('keydown', this._onKey, { passive: true });
  },

  destroyed() {
    if (this._onKey) window.removeEventListener('keydown', this._onKey);
  }
};

export const MenuCalendarOrientation = {
  mounted() {
    const query = '(max-width: 1279px), (max-height: 767px)';
    this._mql = window.matchMedia ? window.matchMedia(query) : null;

    const compute = () => {
      const mode = (this.el && this.el.dataset && this.el.dataset.mode) || 'microsite';
      if (mode !== 'kiosk') {
        return 'days_as_columns';
      }

      if (this._mql) {
        return this._mql.matches ? 'periods_as_columns' : 'days_as_columns';
      }

      const w = window.innerWidth || document.documentElement.clientWidth || 0;
      const h = window.innerHeight || document.documentElement.clientHeight || 0;
      const compact = w <= 1279 || h <= 767;
      return compact ? 'periods_as_columns' : 'days_as_columns';
    };

    const set = (ori) => {
      if (this._orientation === ori) return;
      this._orientation = ori;
      try {
        this.pushEventTo(this.el, 'kiosk:set_orientation', { orientation: ori });
      } catch (e) {
        console.warn('MenuCalendarOrientation push failed:', e);
      }
    };

    const onChange = () => set(compute());
    set(compute());

    if (this._mql) {
      if (typeof this._mql.addEventListener === 'function') {
        this._mql.addEventListener('change', onChange, { passive: true });
        this._onChange = (e) => onChange(e);
      } else if (typeof this._mql.addListener === 'function') {
        this._mql.addListener(onChange);
        this._onChange = onChange;
      }
    } else {
      this._onResize = () => {
        clearTimeout(this._t);
        this._t = setTimeout(() => set(compute()), 150);
      };
      window.addEventListener('resize', this._onResize, { passive: true });
    }
  },

  destroyed() {
    try {
      if (this._mql && this._onChange) {
        if (typeof this._mql.removeEventListener === 'function') {
          this._mql.removeEventListener('change', this._onChange, { passive: true });
        } else if (typeof this._mql.removeListener === 'function') {
          this._mql.removeListener(this._onChange);
        }
      }

      if (this._onResize) {
        window.removeEventListener('resize', this._onResize, { passive: true });
      }
    } catch (_) { }

    clearTimeout(this._t);
  }
};

export const AlpineHook = {
  mounted() {
    Alpine.initTree(this.el);
  }
};

export const KioskTechAccess = {
  mounted() {
    this.cooldownMs = parseInt(this.el.dataset.techCooldown || "3000", 10);
    this.doubleTapWindowMs = 450;
    this.lastTriggeredAt = 0;
    this.lastPointerAt = 0;
    this.source = this.el.dataset.techSource || "footer_logo";

    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);

    this.pointerListenerOptions = { passive: true };

    this.el.addEventListener("dblclick", this.handleDoubleClick);
    this.el.addEventListener("pointerdown", this.handlePointerDown, this.pointerListenerOptions);
  },

  destroyed() {
    this.el.removeEventListener("dblclick", this.handleDoubleClick);
    this.el.removeEventListener("pointerdown", this.handlePointerDown, this.pointerListenerOptions);
  },

  handleDoubleClick(event) {
    event.preventDefault();
    this.triggerAccess("mouse");
  },

  handlePointerDown(event) {
    if (!(event.pointerType === "touch" || event.pointerType === "pen")) {
      return;
    }

    const now = Date.now();
    if (now - this.lastPointerAt <= this.doubleTapWindowMs) {
      this.triggerAccess(event.pointerType);
      this.lastPointerAt = 0;
    } else {
      this.lastPointerAt = now;
    }
  },

  triggerAccess(origin) {
    const now = Date.now();
    if (now - this.lastTriggeredAt < this.cooldownMs) {
      return;
    }

    this.lastTriggeredAt = now;
    this.pushEvent("technician_access_requested", {
      source: this.source,
      origin: origin || "unknown"
    });
  }
};

export const LiveViewMiscRuntimeHooks = {
  InfiniteScroll,
  BackupMealPickerHook,
  ModalManager,
  LiveSearch,
  Twemoji,
  FlashDismiss,
  PreserveCheckboxes,
  IndeterminateCheckbox,
  AutoDismissFlash,
  Clipboard,
  DepartmentTree,
  PrintPreview,
  PrintPreviewTrigger,
  MealSelector,
  MenuCalendarOrientation,
  AlpineHook,
  KioskTechAccess
};
